import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { isNotFoundError } from '@/graphql/errors';
import { useReportError } from '@/hooks/useReportError';
import { ChatComposerContextItem } from '@/modules/common/Chat/components/ChatComposerContextBar';
import { useFeatureFlag } from '@/modules/featureFlags/useFeatureFlag';

import {
  HouseholdChatViewedContextQuery,
  useHouseholdChatViewedContextQuery,
} from '../graphql/HouseholdChatViewedContext.generated';
import { resolveViewedContextFromPathname } from './householdChatViewedContext.utils';

/**
 * The object the user is currently viewing, resolved to a displayable
 * context chip for the chat composer.
 */
export interface HouseholdChatViewedContext {
  /**
   * Stable identity of the viewed object (type + node id), used to scope a
   * dismissal to this object: navigating to a different object produces a
   * different key.
   */
  key: string;
  /**
   * Node ID of the viewed object, sent as the message's context object.
   */
  objectId: string | null;
  item: ChatComposerContextItem;
}

function getNodeDisplayName(
  node: HouseholdChatViewedContextQuery['node']
): string | null {
  switch (node?.__typename) {
    case 'Document':
    case 'Memo':
      return node.name;
    case 'Entity':
      return node.subtype.displayName;
    case 'EstateWaterfall':
      return node.displayName;
    case 'ClientPresentationV2':
      return node.name;
    default:
      return null;
  }
}

/**
 * Derives the viewed-object context from the current route. It matches the
 * pathname to a supported object and resolves its display name.
 *
 * Returns null while the name is loading and on pages with no attachable
 * object, and updates automatically on navigation so the attached context
 * never goes stale.
 */
export function useHouseholdChatViewedContext(): HouseholdChatViewedContext | null {
  const location = useLocation();
  const { reportError } = useReportError();
  // Presentation context must not expose presentation tools before their
  // backend feature flag is enabled for the tenant.
  const presentationToolsEnabled = useFeatureFlag(
    'household_chat_presentation_tools'
  );

  const resolution = useMemo(
    () =>
      resolveViewedContextFromPathname(
        location.pathname,
        presentationToolsEnabled
      ),
    [location.pathname, presentationToolsEnabled]
  );

  const { data } = useHouseholdChatViewedContextQuery({
    variables: { nodeId: resolution?.nodeId ?? '' },
    skip: !resolution?.nodeId,
    onError: (queryError) => {
      // A missing object is expected rather than a failure: deleting the viewed
      // object refetches this query with its now-deleted id before the app
      // navigates away. Drop the chip silently in that case.
      if (isNotFoundError(queryError)) {
        return;
      }

      // Any other failed lookup only means no chip is shown; don't surface it
      // to the user, but do report it so misconfigured routes are diagnosable.
      reportError(
        'Failed to resolve household chat viewed-context name',
        queryError
      );
    },
  });

  return useMemo(() => {
    if (!resolution) {
      return null;
    }

    // Right after navigating between objects, `data` can still hold the
    // previous object's result while the new lookup is in flight; only pair
    // a fetched name with the resolution it belongs to.
    const fetchedName =
      data?.node?.id === resolution.nodeId
        ? getNodeDisplayName(data.node)
        : null;
    if (!fetchedName) {
      return null;
    }

    return {
      key: `${resolution.type}:${resolution.nodeId}`,
      objectId: resolution.nodeId,
      item: { type: resolution.type, name: fetchedName },
    };
  }, [data, resolution]);
}
